/**
 * SPDX-FileCopyrightText: 2024 Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

import type { AxiosError } from '@nextcloud/axios'
import type { SentResults } from '../Api/modules/shares.ts'
import type { User } from '../Types/index.ts'
import type {
	PublicPollEmailConditions,
	Share,
	SharePurpose,
	SharesStore,
} from './shares.types'

import { defineStore } from 'pinia'
import { SharesAPI } from '../Api/index.ts'
import { NotAllowed } from '../Exceptions/Exceptions.ts'
import { Logger } from '../helpers/modules/logger.ts'
import { usePollStore } from './poll.ts'
import { usePollGroupsStore } from './pollGroups.ts'
import { useSessionStore } from './session.ts'

export const useSharesStore = defineStore('shares', {
	state: (): SharesStore => ({
		shares: [],
	}),

	getters: {
		active: (state) => {
			// share types, which will be active, after the user gets his invitation
			const invitationTypes = ['email', 'external', 'contact']

			// sharetype which are active without sending an invitation
			const directShareTypes = ['user', 'group', 'admin', 'public']
			return state.shares.filter(
				(share) =>
					!share.locked
					&& (directShareTypes.includes(share.type)
						|| (invitationTypes.includes(share.type)
							&& (share.type === 'external'
								|| share.invitationSent
								|| share.voted))),
			)
		},

		locked: (state) => state.shares.filter((share) => !!share.locked),
		unsentInvitations: (state) =>
			state.shares.filter(
				(share) =>
					(share.user.emailAddress
						|| share.type === 'group'
						|| share.type === 'contactGroup'
						|| share.type === 'circle')
					&& !share.invitationSent
					&& !share.locked
					&& !share.voted,
			),
		public: (state) => state.shares.filter((share) => share.type === 'public'),
		hasShares: (state) => state.shares.length > 0,
		hasLocked() {
			return this.locked.length > 0
		},
	},

	actions: {
		async load(purpose: SharePurpose = 'poll'): Promise<void> {
			let pollOrPollGroupId: number

			if (purpose === 'pollGroup') {
				const pollGroupsStore = usePollGroupsStore()
				if (
					pollGroupsStore.currentPollGroup?.owner.id
					!== useSessionStore().currentUser.id
				) {
					this.shares = []
					throw new NotAllowed('Not allowed to load shares for this group')
				}

				Logger.info('Loading group shares')
				// For group shares, we need to use the current poll group ID

				if (!pollGroupsStore.currentPollGroup) {
					throw new Error('Current group is not set')
				}
				pollOrPollGroupId = pollGroupsStore.currentPollGroup.id
			} else {
				const pollStore = usePollStore()
				if (!pollStore.permissions.edit) {
					this.shares = []
					throw new NotAllowed('Not allowed to load shares for this poll')
				}
				Logger.info('Loading poll shares')
				// For regular poll shares, we use the current poll ID
				const sessionStore = useSessionStore()
				pollOrPollGroupId = sessionStore.currentPollId
			}

			try {
				const response = await SharesAPI.getShares(
					pollOrPollGroupId,
					purpose,
				)
				this.shares = response.data.shares
			} catch (error) {
				if ((error as AxiosError)?.code !== 'ERR_CANCELED') {
					Logger.error('Error loading shares', {
						error,
						pollId: pollOrPollGroupId,
					})
				}
			}
		},

		async add(user: User, purpose: SharePurpose = 'poll'): Promise<void> {
			let pollOrPollGroupId: number

			if (purpose === 'pollGroup') {
				const pollGroupsStore = usePollGroupsStore()
				// For group shares, we need to use the current poll group ID

				if (!pollGroupsStore.currentPollGroup) {
					throw new Error('Current group is not set')
				}

				pollOrPollGroupId = pollGroupsStore.currentPollGroup.id
			} else {
				// For regular poll shares, we use the current poll ID
				const sessionStore = useSessionStore()
				pollOrPollGroupId = sessionStore.currentPollId
			}

			try {
				const response = await SharesAPI.addUserShare(
					pollOrPollGroupId,
					user,
					purpose,
				)
				this.pushOrUpdate(response.data)
			} catch (error) {
				this.handleError(error, 'Error adding user share', {
					purpose,
					id: pollOrPollGroupId,
					payload: user,
				})
			}
		},

		async addPublicShare(): Promise<void> {
			const sessionStore = useSessionStore()
			try {
				const response = await SharesAPI.addPublicShare(
					sessionStore.currentPollId,
				)
				this.pushOrUpdate(response.data)
			} catch (error) {
				this.handleError(error, 'Error adding public share', {
					pollId: sessionStore.currentPollId,
				})
			}
		},

		pushOrUpdate(payload: { share: Share }): void {
			const foundIndex = this.shares.findIndex(
				(share: Share) => share.id === payload.share.id,
			)
			if (foundIndex === -1) {
				this.shares.push(payload.share)
				return
			}
			Object.assign(this.shares[foundIndex], payload.share)
		},

		async switchAdmin(payload: { share: Share }): Promise<void> {
			const setTo = payload.share.type === 'user' ? 'admin' : 'user'

			try {
				const response = await SharesAPI.switchAdmin(
					payload.share.token,
					setTo,
				)
				this.pushOrUpdate(response.data)
			} catch (error) {
				this.handleError(error, `Error switching type to ${setTo}`, payload)
			}
		},

		async setPublicPollEmail(payload: {
			share: Share
			value: PublicPollEmailConditions
		}): Promise<void> {
			try {
				const response = await SharesAPI.setEmailAddressConstraint(
					payload.share.token,
					payload.value,
				)
				this.pushOrUpdate(response.data)
			} catch (error) {
				this.handleError(
					error,
					'Error changing email register setting',
					payload,
				)
			}
		},

		async writeLabel(payload: { token: string; label: string }): Promise<void> {
			try {
				const response = await SharesAPI.writeLabel(
					payload.token,
					payload.label,
				)
				this.pushOrUpdate(response.data)
			} catch (error) {
				this.handleError(error, 'Error writing label', payload)
			}
		},

		async inviteAll(payload: { pollId: number }): Promise<SentResults> {
			try {
				const response = await SharesAPI.inviteAll(payload.pollId)
				this.load()
				return response.data.sentResult
			} catch (error) {
				this.handleError(error, 'Error inviting all users', payload)
			}
			return {
				sentMails: [],
				abortedMails: [],
			}
		},

		async sendInvitation(payload: { share: Share }): Promise<{
			share: Share
			sentResult: null | SentResults
		} | void> {
			try {
				const response = await SharesAPI.sendInvitation(payload.share.token)
				this.load()
				return response.data
			} catch (error) {
				this.handleError(error, 'Error sending share invitation', payload)
			}
		},

		async resolveGroup(payload: { share: Share }): Promise<void> {
			try {
				await SharesAPI.resolveShare(payload.share.token)
				this.load()
			} catch (error) {
				this.handleError(error, 'Error resolving group share', payload)
			}
		},

		async lock(payload: { share: Share }): Promise<void> {
			try {
				const response = await SharesAPI.lockShare(payload.share.token)
				this.pushOrUpdate(response.data)
			} catch (error) {
				this.handleError(error, 'Error locking share', payload)
			}
		},

		async unlock(payload: { share: Share }): Promise<void> {
			try {
				const response = await SharesAPI.unlockShare(payload.share.token)
				this.pushOrUpdate(response.data)
			} catch (error) {
				this.handleError(error, 'Error unlocking share', payload)
			}
		},

		async delete(payload: { share: Share }): Promise<void> {
			try {
				const response = await SharesAPI.deleteShare(payload.share.token)
				this.pushOrUpdate(response.data)
			} catch (error) {
				this.handleError(error, 'Error deleting share', payload)
			}
		},
		async restore(payload: { share: Share }): Promise<void> {
			try {
				const response = await SharesAPI.restoreShare(payload.share.token)
				this.pushOrUpdate(response.data)
			} catch (error) {
				this.handleError(error, 'Error restoring share', payload)
			}
		},

		handleError(error: unknown, message: string, payload?: unknown): void {
			if ((error as AxiosError)?.code === 'ERR_CANCELED') return

			Logger.error(message, {
				error,
				payload,
			})

			this.load()
			throw error
		},
	},
})
